import { Table } from "antd";
import { map, filter, sortBy, isEmpty } from "lodash";
import "./measures.scss";
import Filter from "./filters/filter";
import { useCallback, useEffect, useState } from "react";
import reportService from "@services/report.service";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { TableLocale } from "@components/noDataFound";
import ColumnContentRenderer from "./columnFormat";
import dashboardService from "@services/dashboard.service";
import { useMeasuresStore } from "@stores/measuresStore";
import { usePopulationInsightStore } from "@stores/populationInsightStore";

const MeasureReport = () => {
  const [initialLoad, setinitialLoad] = useState(true);
  const [loading, setLoading] = useState(true);
  const [columnsLoading, setColumnsLoading] = useState(true);
  const [tableColumn, setTableColumn] = useState<any>([]);
  const [columnsList, setColumnsList] = useState<any>([]);
  const [tableData, setTableData] = useState<any>([]);
  const selectedProvider = useMeasuresStore((state) => state.selectedProvider);
  const selectedPCPVendor = useMeasuresStore((state) => state.selectedPCPVendor);
  const selectedHealthPlan = useMeasuresStore((state) => state.selectedHealthPlan);
  const handleComingFromMembersFlag = usePopulationInsightStore((state) => state.handleComingFromMembersFlag);
  const appliedProvider = useMeasuresStore((state) => state.appliedProvider);
  const setAppliedProvider = useMeasuresStore((state) => state.setAppliedProvider);
  const comingFromMembers = usePopulationInsightStore((state) => state.comingFromMembers);
  const selectedYear = useMeasuresStore((state) => state.selectedYear);
  const setSelectedYear = useMeasuresStore((state) => state.setSelectedYear);

  const currentYear = new Date().getFullYear();
  const startYear = 2024;
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, index) => ({
    id: currentYear - index,
    value: (currentYear - index).toString(),
  }));

  const fetchData = async (healthPlan: any, pcpVendor: any, pcpId: any, year: string) => {
    setLoading(true);
    dashboardService.getMeasuresReportInfo(pcpId, pcpVendor, healthPlan, year).subscribe({
      next: (v) => {
        setTableData(v?.data?.data);
        setLoading(false);
        handleComingFromMembersFlag(false);
      },
      error: (e) => {
        console.log(e);
        setLoading(false);
      },
    });
  };

  const fetchTableColumns = useCallback(() => {
    const pageName = "MEASURES-DASHBOARD";
    setColumnsLoading(true);

    reportService.getColumnsList(pageName).subscribe({
      next: (v) => {
        let _columnsNeedToShowInTable = filter(v.data.data?.columns, (item) => {
          return item.isDisplay === "Y";
        });

        setTableColumn(_columnsNeedToShowInTable);
        setColumnsLoading(false);
      },
      error: (e) => {
        console.log(e);
        setColumnsLoading(false);
      },
    });
  }, [setTableColumn]);

  useEffect(() => {
    fetchTableColumns();
  }, [fetchTableColumns]);

  useEffect(() => {
    if (initialLoad && !isEmpty(selectedProvider)) {
      if (comingFromMembers) {
        fetchData(appliedProvider[0], appliedProvider[1], appliedProvider[2], selectedYear);
      } else {
        setAppliedProvider([selectedHealthPlan, selectedPCPVendor, selectedProvider]);
        fetchData(selectedHealthPlan, selectedPCPVendor, selectedProvider, selectedYear);
      }
      setinitialLoad(false);
    }
  }, [initialLoad, selectedPCPVendor, selectedProvider]);

  useEffect(() => {
    let _columns = map(tableColumn, (item, index: number) => {
      const formatTypes = item.format_type ? item.format_type.split("$|") : [];
      const measureFormatTypes = ["STAR", "DESCRIPTION", "SUBDESCRIPTION"];

      const columnConfig = {
        ...item,
        align: "left",
        ellipsis: true,
        title: item.tableHeaderName,
        dataIndex: item.dataIndex,
        key: item.key,
        sorter: (a: any, b: any) => {
          const valueA = a[item.dataIndex];
          const valueB = b[item.dataIndex];

          if (measureFormatTypes.some((type) => formatTypes.includes(type))) {
            return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
          } else {
            return valueA - valueB;
          }
        },

        render: (value: any, record: any) => {
          return <ColumnContentRenderer value={value} formatTypes={formatTypes} record={record} column={item} selectedYear={selectedYear}/>;
        },
      };

      if (index === 0) {
        columnConfig.width = 700;
      }

      return columnConfig;
    });

    _columns = sortBy(_columns, (item: any) => {
      return item.order;
    });

    setColumnsList(_columns);
  }, [tableColumn, selectedYear]);

  const handleYearChange = (value: string) => {
    setSelectedYear(value);
    fetchData(selectedHealthPlan, selectedPCPVendor, selectedProvider, value);
  };

  useEffect(() => {
    if (!selectedYear) {
      setSelectedYear(years[0].value);
    }
  }, []);

  return (
    <div className="measure-report">
      <div className="measure-report__header">
        <div className="measure-report__header__title">Measures</div>
        <div className="measure-report__header__filter">
          <Filter columns={columnsList} tableData={tableData} fetchData={fetchData} years={years} selectedYear={selectedYear} onYearChange={handleYearChange} isDataLoading={loading}/>
        </div>
      </div>
      <div className="measure-report__body">
        <span className="measure-report__body__subtitle">
          Measurement Year Jan {selectedYear} to {selectedYear === currentYear.toString() ? "Current Month" : `Dec ${selectedYear}`}
        </span>
        <Table
          bordered={true}
          columns={columnsList}
          dataSource={tableData}
          className="measure-report__body__table"
          pagination={false}
          loading={{ spinning: loading || columnsLoading, indicator: <HealthemSpinner /> }}
          locale={TableLocale}
        />
        <div className="measure-report__body__footer">
          <span className="measure-report__body__required-star">*</span>
          <span className="measure-report__body__footer__label">A lower rate indicates better performance for this indicator</span>
        </div>
      </div>
    </div>
  );
};
export default MeasureReport;
